import { getApiError } from '@/composables/getApiError'
import { BASE_URL } from '@/environment/environment'
import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import type { IFilterChoiceItem } from '@/modules/universalStats/types/IFilterChoiceItem'
import axios from 'axios'
import { defineStore } from 'pinia'
import type {
  IDepartmentEmployeeItem,
  IDepartmentEmployeesResponse,
} from '../types/settings/IStaffDepartmentEmployeesResponse'
import type { IDepartmentCreateUpdatePayload } from '../types/settings/IStaffDepartmentFormData'
import type {
  IStaffDepartmentDetail,
  IStaffDepartmentLocation,
  IStaffDepartmentTreeNode,
} from '../types/settings/IStaffDepartmentSettings'
import type {
  IPositionEmployeeItem,
  IPositionEmployeesResponse,
} from '../types/settings/IStaffPositionEmployeesResponse'
import type { IStaffPositionFormData } from '../types/settings/IStaffPositionFormData'
import type {
  IStaffPositionDetail,
  IStaffPositionFilters,
  IStaffPositionListItem
} from '../types/settings/IStaffPositionSettings'
import type { IStaffWithoutDepartmentDetail } from '../types/settings/IStaffWithoutDepartment'

/**
 * Стор администрирования структуры персонала (Staff Settings Store).
 *
 * Управляет:
 * - Деревом и иерархической структурой отделений/филиалов (CRUD, поиск, перемещение);
 * - Локациями и привязками сотрудников к отделениям;
 * - Должностями (CRUD, фильтрация, пагинация по курсору, просмотр прикрепленных сотрудников);
 * - Индикаторами загрузки и сохранения для административных форм персонала.
 */
export const useStaffSettingsStore = defineStore('staffSettingsStore', {
  state: () => ({
    // Отделения
    /** Иерархическое дерево структуры отделений (полная структура) */
    departmentsTree: [] as IStaffDepartmentTreeNode[],
    /** Результат поиска по дереву структуры отделений */
    searchDepartmentsTree: [] as IStaffDepartmentTreeNode[],
    /** Детальная информация о выбранном отделении */
    currentDepartmentDetail: null as IStaffDepartmentDetail | null,
    /** Список доступных физических локаций/адресов */
    locationsList: [] as IStaffDepartmentLocation[],
    /** Список сотрудников конкретного отделения */
    departmentEmployees: [] as IDepartmentEmployeeItem[],
    /** Общее количество сотрудников в выбранном отделении */
    departmentEmployeesCount: 0,
    /** Ссылка next курсорной страницы сотрудников отделения */
    departmentEmployeesNext: null as string | null,
    /** Информация о сотрудниках без отделения (счётчик) */
    withoutDepartmentInfo: null as IStaffWithoutDepartmentDetail | null,
    /** Поисковый запрос по структуре */
    searchQuery: '',

    // Должности
    /** Список должностей для отображения в таблице */
    positionsList: [] as IStaffPositionListItem[],
    /** Упрощенный список всех должностей для выпадающих списков */
    allPositionsSimple: [] as IStaffPositionListItem[],
    /** Общее количество должностей */
    totalPositionsCount: 0,
    /** Курсор для следующей страницы должностей */
    nextPositionsCursor: null as string | null,
    /** Поле и направление сортировки должностей */
    currentPositionsSortString: 'name' as string | null,
    /** Количество элементов на страницу */
    itemsPerPage: 25,
    /** Фильтры таблицы должностей */
    positionsFilters: {
      search: '',
    } as IStaffPositionFilters,
    /** Детальная информация о выбранной должности */
    currentPositionDetail: null as IStaffPositionDetail | null,
    /** Список сотрудников, занимающих выбранную должность */
    positionEmployees: [] as IPositionEmployeeItem[],
    /** Количество сотрудников на должности */
    positionEmployeesCount: 0,
    /** Ссылка next курсорной страницы сотрудников должности */
    positionEmployeesNext: null as string | null,

    // Фильтр-чойсы для UniversalFilterTree
    filterChoices: {} as Record<string, IFilterChoiceItem[]>,
    searchChoices: {} as Record<string, IFilterChoiceItem[]>,
    filterCursors: {} as Record<string, string | null>,
    filterLoading: {} as Record<string, boolean>,
    filterStrategies: {} as Record<string, string>,
  }),

  actions: {
    /**
     * Загружает дерево отделений с поддержкой серверного поиска (?search=)
     */
    async fetchDepartmentsTree(search?: string, options?: { silent?: boolean }): Promise<IStaffDepartmentTreeNode[]> {
      const feedbackStore = useFeedbackStore()
      if (!options?.silent)
        feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}
        const query = (search !== undefined ? search : this.searchQuery)?.trim()
        if (query) {
          params.search = query
        }

        const response = await axios.get<{ results?: IStaffDepartmentTreeNode[] } | IStaffDepartmentTreeNode[]>(
          onboardingApiUrl('employee', '/employee/management/department/'),
          { params },
        )

        const tree = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        if (query) {
          this.searchDepartmentsTree = tree
        } else {
          this.departmentsTree = tree
          this.searchDepartmentsTree = []
        }
        return tree
      } catch (error) {
        console.error('❌ Ошибка загрузки дерева отделений:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить структуру отделений'),
          time: 5000,
        })
        return []
      } finally {
        if (!options?.silent)
          feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает детальную информацию об отделении
     */
    async fetchDepartmentDetail(id: string): Promise<IStaffDepartmentDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<IStaffDepartmentDetail>(
          onboardingApiUrl('employee', `/employee/management/department/${id}/`),
        )

        this.currentDepartmentDetail = response.data
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка получения деталей отделения ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные отделения'),
          time: 5000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает список локаций (корпуса/помещения) для выбора в форме
     */
    async fetchLocations(): Promise<IStaffDepartmentLocation[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<{ results?: IStaffDepartmentLocation[] } | IStaffDepartmentLocation[]>(
          `${BASE_URL}/api/hospital/locations/`,
        )

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        this.locationsList = list
        return list
      } catch (error) {
        console.error('❌ Ошибка загрузки списка локаций:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список локаций'),
          time: 5000,
        })
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создает новое подразделение
     */
    async createDepartment(payload: IDepartmentCreateUpdatePayload): Promise<IStaffDepartmentTreeNode | null> {
      const feedbackStore = useFeedbackStore()
      const apiStore = useApiStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IStaffDepartmentTreeNode>(
          `${BASE_URL}/api/employee/management/department/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Отделение успешно создано',
          time: 4000,
        })

        await this.fetchDepartmentsTree()
        apiStore.fetchAllDepartments()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания отделения:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать отделение'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет параметры подразделения
     */
    async updateDepartment(
      id: string,
      payload: Partial<IDepartmentCreateUpdatePayload>,
    ): Promise<IStaffDepartmentTreeNode | null> {
      const feedbackStore = useFeedbackStore()
      const apiStore = useApiStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<IStaffDepartmentTreeNode>(
          `${BASE_URL}/api/employee/management/department/${id}/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Изменения успешно сохранены',
          time: 4000,
        })

        await this.fetchDepartmentsTree()
        apiStore.fetchAllDepartments()
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка обновления отделения ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет подразделение
     */
    async deleteDepartment(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      const apiStore = useApiStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/employee/management/department/${id}/`)

        feedbackStore.showToast({
          type: 'success',
          message: 'Отделение успешно удалено',
          time: 4000,
        })

        await this.fetchDepartmentsTree()
        apiStore.fetchAllDepartments()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления отделения ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить отделение'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает сотрудников конкретного отделения
     */
    async fetchDepartmentEmployees(
      departmentId: string,
      search?: string,
      cursor?: string,
      limit?: number,
      sort?: string | null,
      filters?: Record<string, any>,
    ): Promise<IDepartmentEmployeeItem[]> {
      const feedbackStore = useFeedbackStore()
      const isNextPage = Boolean(cursor)
      if (!isNextPage)
        feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}
        if (search?.trim())
          params.search = search.trim()
        if (cursor)
          params.cursor = cursor
        if (limit)
          params.page_size = limit
        if (sort)
          params.sort = sort

        if (filters) {
          if (filters.positions?.length) {
            params.positions = Array.isArray(filters.positions) ? filters.positions.join(',') : filters.positions
          }
          if (filters.departments?.length) {
            params.departments = Array.isArray(filters.departments) ? filters.departments.join(',') : filters.departments
          }
          if (filters.roles?.length) {
            params.roles = Array.isArray(filters.roles) ? filters.roles.join(',') : filters.roles
          }
          if (filters.status?.length) {
            params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status
          }
        }

        const endpoint = departmentId === 'without-department'
          ? onboardingApiUrl('employee', '/employee/management/department/without-department/employees/')
          : onboardingApiUrl('employee', `/employee/management/department/${departmentId}/employees/`)

        const response = await axios.get<IDepartmentEmployeesResponse>(
          endpoint,
          { params },
        )

        const results = response.data?.results || []
        this.departmentEmployees = isNextPage
          ? [...this.departmentEmployees, ...results]
          : results
        this.departmentEmployeesCount = response.data?.count ?? this.departmentEmployees.length
        this.departmentEmployeesNext = response.data?.next ?? null
        return results
      } catch (error) {
        console.error(`❌ Ошибка загрузки сотрудников отделения ${departmentId}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список сотрудников отделения'),
          time: 5000,
        })
        if (!isNextPage) {
          this.departmentEmployees = []
          this.departmentEmployeesCount = 0
          this.departmentEmployeesNext = null
        }
        return []
      } finally {
        if (!isNextPage)
          feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Привязывает сотрудников к отделению
     */
    async addDepartmentEmployees(
      departmentId: string,
      employeeIds: string[],
    ): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<{ success: boolean; detail?: string; added_count?: number }>(
          `${BASE_URL}/api/employee/management/department/${departmentId}/employees/`,
          { employee_ids: employeeIds },
        )

        feedbackStore.showToast({
          type: 'success',
          message: response.data?.detail || 'Сотрудники успешно добавлены в отделение',
          time: 4000,
        })

        await this.fetchDepartmentEmployees(departmentId)
        await this.fetchDepartmentsTree()
        await this.fetchWithoutDepartmentInfo()
        return true
      } catch (error) {
        console.error(`❌ Ошибка добавления сотрудников в отделение ${departmentId}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось добавить сотрудников в отделение'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает информацию о сотрудниках без отделения (счётчик и метаданные)
     */
    async fetchWithoutDepartmentInfo(options?: { silent?: boolean }): Promise<IStaffWithoutDepartmentDetail | null> {
      const feedbackStore = useFeedbackStore()
      if (!options?.silent)
        feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<IStaffWithoutDepartmentDetail>(
          onboardingApiUrl('employee', '/employee/management/department/without-department/'),
        )
        this.withoutDepartmentInfo = response.data
        return response.data
      } catch (error) {
        console.error('❌ Ошибка загрузки информации о сотрудниках без отделения:', error)
        this.withoutDepartmentInfo = null
        if (!options?.silent) {
          feedbackStore.showToast({
            type: 'error',
            message: getApiError(error, 'Не удалось загрузить информацию о сотрудниках без отделения'),
            time: 5000,
          })
        }
        return null
      } finally {
        if (!options?.silent)
          feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Отвязывает сотрудников от отделений (переводит в категорию «Без отделения»)
     */
    async unassignDepartmentEmployees(employeeIds: string[]): Promise<boolean> {
      if (!employeeIds.length) return false
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<{ success: boolean; detail?: string; added_count?: number }>(
          `${BASE_URL}/api/employee/management/department/without-department/employees/`,
          { employee_ids: employeeIds },
        )

        feedbackStore.showToast({
          type: 'success',
          message: response.data?.detail || 'Сотрудники успешно переведены в статус «Без отделения»',
          time: 4000,
        })

        await this.fetchWithoutDepartmentInfo()
        await this.fetchDepartmentsTree()
        return true
      } catch (error) {
        console.error('❌ Ошибка отвязки сотрудников от отделения:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось переместить сотрудников в «Без отделения»'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Отвязывает сотрудников от отделения (одного или нескольких)
     */
    async removeDepartmentEmployees(
      departmentId: string,
      employeeIds: string[],
    ): Promise<boolean> {
      if (!employeeIds.length) return false
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.delete<{ success: boolean; detail?: string; removed_count?: number }>(
          `${BASE_URL}/api/employee/management/department/${departmentId}/employees/`,
          {
            params: { employee_ids: employeeIds.join(',') },
            data: { employee_ids: employeeIds },
          },
        )

        const count = response.data?.removed_count ?? employeeIds.length
        feedbackStore.showToast({
          type: 'success',
          message: employeeIds.length > 1
            ? `Сотрудники (${count}) успешно удалены из отделения`
            : 'Сотрудник успешно удален из отделения',
          time: 4000,
        })

        await this.fetchDepartmentEmployees(departmentId)
        await this.fetchDepartmentsTree()
        await this.fetchWithoutDepartmentInfo()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления сотрудников из отделения ${departmentId}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить сотрудников из отделения'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Отвязывает одного сотрудника от отделения
     */
    async removeDepartmentEmployee(
      departmentId: string,
      employeeId: string,
    ): Promise<boolean> {
      return this.removeDepartmentEmployees(departmentId, [employeeId])
    },

    // ==========================================
    // ДЕЙСТВИЯ: ДОЛЖНОСТИ (POSITIONS)
    // ==========================================

    /**
     * Загружает полный список должностей с поддержкой поиска и сортировки
     */
    async fetchPositions(options?: { silent?: boolean }): Promise<IStaffPositionListItem[] | null> {
      const feedbackStore = useFeedbackStore()
      if (!options?.silent)
        feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}

        if (this.positionsFilters.search?.trim()) {
          params.search = this.positionsFilters.search.trim()
        }

        if (this.currentPositionsSortString) {
          params.ordering = this.currentPositionsSortString
        }

        const response = await axios.get<{ results?: IStaffPositionListItem[], count?: number } | IStaffPositionListItem[]>(
          onboardingApiUrl('employee', '/employee/management/position/'),
          { params },
        )

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        this.positionsList = list
        this.totalPositionsCount = (response.data as any)?.count ?? list.length
        this.nextPositionsCursor = null

        return list
      } catch (error) {
        console.error('❌ Ошибка загрузки списка должностей:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список должностей'),
          time: 5000,
        })
        return null
      } finally {
        if (!options?.silent)
          feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает полный список должностей для выбора в выпадающем списке вышестоящих должностей
     */
    async fetchAllPositionsSimple(): Promise<IStaffPositionListItem[]> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<{ results?: IStaffPositionListItem[] } | IStaffPositionListItem[]>(
          onboardingApiUrl('employee', '/employee/position/'),
        )

        const list = Array.isArray(response.data)
          ? response.data
          : Array.isArray(response.data?.results)
            ? response.data.results
            : []

        this.allPositionsSimple = list
        return list
      } catch (error) {
        console.error('❌ Ошибка загрузки всех должностей:', error)
        return []
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает детальную информацию о должности
     */
    async fetchPositionDetail(id: string): Promise<IStaffPositionDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.get<IStaffPositionDetail>(
          onboardingApiUrl('employee', `/employee/management/position/${id}/`),
        )

        this.currentPositionDetail = response.data
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка получения деталей должности ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить данные должности'),
          time: 5000,
        })
        return null
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Создает новую должность
     */
    async createPosition(payload: IStaffPositionFormData): Promise<IStaffPositionDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.post<IStaffPositionDetail>(
          `${BASE_URL}/api/employee/management/position/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Должность успешно создана',
          time: 4000,
        })

        await this.fetchPositions()
        await this.fetchAllPositionsSimple()
        return response.data
      } catch (error) {
        console.error('❌ Ошибка создания должности:', error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось создать должность'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Обновляет должность
     */
    async updatePosition(id: string, payload: IStaffPositionFormData): Promise<IStaffPositionDetail | null> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        const response = await axios.patch<IStaffPositionDetail>(
          `${BASE_URL}/api/employee/management/position/${id}/`,
          payload,
        )

        feedbackStore.showToast({
          type: 'success',
          message: 'Изменения успешно сохранены',
          time: 4000,
        })

        await this.fetchPositions()
        await this.fetchAllPositionsSimple()
        return response.data
      } catch (error) {
        console.error(`❌ Ошибка обновления должности ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось сохранить изменения'),
          time: 5000,
        })
        throw error
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Удаляет должность
     */
    async deletePosition(id: string): Promise<boolean> {
      const feedbackStore = useFeedbackStore()
      feedbackStore.isGlobalLoading = true
      try {
        await axios.delete(`${BASE_URL}/api/employee/management/position/${id}/`)

        feedbackStore.showToast({
          type: 'success',
          message: 'Должность успешно удалена',
          time: 4000,
        })

        this.positionsList = this.positionsList.filter(p => String(p.id) !== String(id))
        this.totalPositionsCount = this.positionsList.length

        await this.fetchPositions({ silent: true })
        await this.fetchAllPositionsSimple()
        return true
      } catch (error) {
        console.error(`❌ Ошибка удаления должности ${id}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось удалить должность'),
          time: 5000,
        })
        return false
      } finally {
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загружает сотрудников конкретной должности
     */
    async fetchPositionEmployees(
      positionId: string,
      search?: string,
      cursor?: string,
      limit?: number,
      sort?: string | null,
      filters?: Record<string, any>,
    ): Promise<IPositionEmployeeItem[]> {
      const feedbackStore = useFeedbackStore()
      const isNextPage = Boolean(cursor)
      if (!isNextPage)
        feedbackStore.isGlobalLoading = true
      try {
        const params: Record<string, any> = {}
        if (search?.trim())
          params.search = search.trim()
        if (cursor)
          params.cursor = cursor
        if (limit)
          params.page_size = limit
        if (sort)
          params.sort = sort

        if (filters) {
          if (filters.positions?.length) {
            params.positions = Array.isArray(filters.positions) ? filters.positions.join(',') : filters.positions
          }
          if (filters.departments?.length) {
            params.departments = Array.isArray(filters.departments) ? filters.departments.join(',') : filters.departments
          }
          if (filters.roles?.length) {
            params.roles = Array.isArray(filters.roles) ? filters.roles.join(',') : filters.roles
          }
          if (filters.status?.length) {
            params.status = Array.isArray(filters.status) ? filters.status.join(',') : filters.status
          }
        }

        const response = await axios.get<IPositionEmployeesResponse>(
          onboardingApiUrl('employee', `/employee/management/position/${positionId}/employees/`),
          { params },
        )

        const results = response.data?.results || []
        this.positionEmployees = isNextPage
          ? [...this.positionEmployees, ...results]
          : results
        this.positionEmployeesCount = response.data?.count ?? this.positionEmployees.length
        this.positionEmployeesNext = response.data?.next ?? null
        return results
      } catch (error) {
        console.error(`❌ Ошибка загрузки сотрудников должности ${positionId}:`, error)
        feedbackStore.showToast({
          type: 'error',
          message: getApiError(error, 'Не удалось загрузить список сотрудников должности'),
          time: 5000,
        })
        if (!isNextPage) {
          this.positionEmployees = []
          this.positionEmployeesCount = 0
          this.positionEmployeesNext = null
        }
        return []
      } finally {
        if (!isNextPage)
          feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Левый список должностей для сайдбара
     */
    async fetchPositionsForSidebar(options?: { silent?: boolean }): Promise<IStaffPositionListItem[]> {
      const list = await this.fetchPositions(options)
      return list || []
    },

    /**
     * Сброс фильтров должностей
     */
    resetPositionsFilters() {
      this.positionsFilters.search = ''
      this.currentPositionsSortString = 'name'
    },

    /**
     * Загрузка вариантов фильтрации для UniversalFilterTree в настройках персонала
     */
    async fetchFilterChoices(fieldKey: string, search = '', cursor?: string) {
      if (!fieldKey) return
      this.filterLoading[fieldKey] = true

      try {
        const params: Record<string, string | number | boolean | undefined> = { field: fieldKey }

        if (search) {
          params.search_field = search
        }

        if (cursor) {
          params.cursor = cursor
        }

        const response = await axios.get(onboardingApiUrl('employee', '/employee/management/filter-choices/'), { params })
        const { next, strategy } = response.data
        const results = response.data.results

        this.filterStrategies[fieldKey] = strategy || 'independent'

        if (search) {
          if (cursor) {
            this.searchChoices[fieldKey] = [...(this.searchChoices[fieldKey] || []), ...results]
          } else {
            this.searchChoices[fieldKey] = results
          }
        } else {
          if (cursor) {
            this.filterChoices[fieldKey] = [...(this.filterChoices[fieldKey] || []), ...results]
          } else {
            this.filterChoices[fieldKey] = results
          }
        }
        this.filterCursors[fieldKey] = next || null
      } catch (e) {
        console.error(`❌ Ошибка загрузки фильтров сотрудников в staffSettingsStore [${fieldKey}]:`, e)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },
  },
})
